import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import { FlexColumn } from '@/atoms/FlexContainers'
import { UploadIcon } from '@/atoms/Icons/UploadIcon'
import { TitleXS } from '@/atoms/Text'
import { slugs } from '@/constants'
import { useThemeContext } from '@/contexts/ThemeContext'
import { TicketsMainFooter } from '@/organisms/Footer/TicketsMainFooter'
import { ReservationObject, TheatricalReleaseObject, TheatricalShowtimeVenue } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { AddressSearch } from '@/views/TicketCheckoutViews/ShowtimesView/components/AddressSearch'
import { Calendar } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar'
import { ComingSoon } from '@/views/TicketCheckoutViews/ShowtimesView/components/ComingSoon'
import FandangoRedirect from '@/views/TicketCheckoutViews/ShowtimesView/components/FandangoRedirect'
import TicketBannerSection from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketBannerSection'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { TicketsPayItForwardAd } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPayItForwardAd'
import { FreeTicketModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/FreeTicketModal'
import { useTicketsPromoContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsPromo/TicketsPromoContext'
import Venues from '@/views/TicketCheckoutViews/ShowtimesView/components/Venues'
import TicketPromoModals from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/TicketPromoModals'
import { TexasModalProvider } from '@/views/TicketCheckoutViews/ShowtimesView/contexts/TexasModalContext'
import { useFindQueryVenue } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useFindQueryVenue'
import { useVenueSearch } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useVenueSearch'
import { Coordinates } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useVenueSearch/useVenueSearch'

interface TicketSearchProps {
  theatricalSlug: string
  theatricalRelease: TheatricalReleaseObject
  scrollToTop: () => void
  calendarRef: React.RefObject<HTMLDivElement>
  reservation?: ReservationObject
  isEditReservationFlow?: boolean
  showOverlayVideoExperiment?: boolean
}

export const ShowtimesLayout = ({
  theatricalRelease,
  calendarRef,
  theatricalSlug,
  isEditReservationFlow = false,
  reservation,
  scrollToTop,
}: TicketSearchProps) => {
  const { isDarkMode } = useThemeContext()
  const { t } = useTranslate('tickets')
  const searchScrollContainer = useRef<HTMLDivElement>(null)
  const { openSharingModal, handleMobileShare, isFreeTicketPromo } = useTicketsContext()
  const { venue: showtimeSelectedVenue } = useTicketsPromoContext()
  const contentWrapperRef = useRef<HTMLDivElement>(null)
  const [isFortWorth, setIsFortWorth] = useState(false)
  const [shouldOpenTexasModal, setShouldOpenTexasModal] = useState(false)

  const scrollToContentTop = useCallback(() => {
    contentWrapperRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const releaseDate = useMemo(() => {
    try {
      return theatricalRelease?.region?.releaseDate ? new Date(theatricalRelease?.region?.releaseDate) : new Date()
    } catch (err) {
      return new Date()
    }
  }, [theatricalRelease?.region?.releaseDate])

  const {
    searchDate,
    handleDateChange,
    handleAddressChange: originalHandleAddressChange,
    earliestDate,
    venues,
    loading,
    hasSearchedOnce,
    defaultAddressString,
    error,
  } = useVenueSearch({
    releaseDate: releaseDate,
    theatricalSlug,
    countryCode: theatricalRelease?.region?.countryCode ?? 'us',
  })

  const handleAddressChange = useCallback(
    (coordinates: Coordinates, searchString: string) => {
      if (theatricalSlug !== slugs.theLastRodeo) {
        return originalHandleAddressChange(coordinates, searchString)
      }

      const isFortWorthLocation =
        searchString === 'Fort Worth, TX, USA' ||
        searchString.includes('Fort Worth') ||
        defaultAddressString.includes('Fort Worth')

      if (isFortWorthLocation) {
        setIsFortWorth(true)
        setShouldOpenTexasModal(true)
      } else {
        setIsFortWorth(false)
        setShouldOpenTexasModal(false)
      }

      originalHandleAddressChange(coordinates, searchString)
    },
    [originalHandleAddressChange, defaultAddressString, theatricalSlug],
  )

  useEffect(() => {
    if (
      defaultAddressString &&
      (defaultAddressString.includes('Fort Worth') || defaultAddressString === 'Fort Worth, TX, USA')
    ) {
      setIsFortWorth(true)
      if (theatricalSlug === slugs.theLastRodeo) {
        setShouldOpenTexasModal(true)
      }
    }
  }, [defaultAddressString, theatricalSlug])

  const { venue } = useFindQueryVenue({ venues: venues as TheatricalShowtimeVenue[] })

  const theatricalReleaseName = theatricalRelease.title ?? ''
  const shouldShowTicketsComingSoon = theatricalRelease?.region?.ticketsStatus === 'COMING_SOON'
  return (
    <TexasModalProvider theatricalSlug={theatricalSlug} shouldOpenModal={shouldOpenTexasModal}>
      <main
        ref={searchScrollContainer}
        className={classNames(
          'w-full gap-y-2 px-4 lg:overflow-y-scroll',
          isDarkMode ? 'bg-core-gray-950' : 'bg-core-gray-100',
        )}
      >
        <h1 className="sr-only">
          {t('projectTicketsAndShowtimes', '{{projectName}} Tickets & Showtimes', {
            projectName: theatricalRelease?.title,
          })}
        </h1>
        <div className="flex min-h-[50vh] flex-col">
          <div ref={contentWrapperRef} className="pt-6">
            <FlexColumn className="gap-2">
              <div className="flex flex-row flex-wrap items-end justify-between">
                <label htmlFor="search">
                  <TitleXS weight="bold" className="text-core-gray-950">
                    {t('enterCityStateZipV3', '   Search Location')}
                  </TitleXS>
                </label>
                <button className="flex flex-row gap-1" onClick={isMobile ? handleMobileShare : openSharingModal}>
                  <TitleXS weight="semibold" className="my-auto cursor-pointer text-sm text-core-gray-950">
                    {t('share', 'Share')}
                  </TitleXS>
                  <UploadIcon color="core-gray-950" size={18} />
                </button>
              </div>
              <AddressSearch
                countryCode={theatricalRelease?.region?.countryCode ?? 'us'}
                onChange={(coordinates, searchString) => {
                  handleAddressChange(coordinates, searchString)
                }}
                defaultAddressString={defaultAddressString}
                scrollToVenues={scrollToTop}
                scrollToContentTop={scrollToContentTop}
              />
            </FlexColumn>

            <Calendar
              releaseDate={theatricalRelease?.region?.releaseDate}
              className="-mx-4 mt-10 min-h-[120px]"
              disabled={false}
              onChange={handleDateChange}
              selectedDate={searchDate}
              startDate={earliestDate}
              scrollToTop={scrollToTop}
              calendarRef={calendarRef}
              theatricalSlug={theatricalSlug}
              theatricalRelease={theatricalRelease}
            />
            <TicketBannerSection
              reservation={reservation}
              isEditReservationFlow={isEditReservationFlow}
              theatricalRelease={theatricalRelease}
              venues={venues as TheatricalShowtimeVenue[]}
            />
          </div>
          {shouldShowTicketsComingSoon ? (
            <ComingSoon slug={theatricalRelease?.theatricalSlug} />
          ) : (
            <Venues
              venues={venues as TheatricalShowtimeVenue[]}
              theatricalRelease={theatricalRelease}
              isLoading={loading || !hasSearchedOnce}
              reservation={reservation}
            />
          )}
        </div>
        <TicketsMainFooter
          className="-mx-4 mt-6"
          slug={theatricalRelease.projectSlug}
          projectName={theatricalReleaseName}
        />
      </main>
      <TicketsPayItForwardAd
        theatricalSlug={theatricalSlug}
        theatricalRelease={theatricalRelease}
        className="justify-center text-center md:hidden"
      />
      <FandangoRedirect
        fandangoUrl={theatricalRelease?.region?.ticketsEmergencyRedirectUrl ?? ''}
        shouldRedirectToFandango={!!error && !shouldShowTicketsComingSoon}
      />

      {isFreeTicketPromo && (
        <FreeTicketModal
          maxTickets={theatricalRelease?.region?.freeTicketRequestAmount ?? 1}
          venue={showtimeSelectedVenue ?? venue}
        />
      )}
      <TicketPromoModals
        projectName={theatricalRelease?.title}
        theatricalSlug={theatricalSlug}
        isFortWorth={isFortWorth}
      />
    </TexasModalProvider>
  )
}
